import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { supabase } from '../../supabase';
import { APP_ROUTES } from '../../utils/auth';
import {
  clearStoredDriverSession,
  getStoredDriver,
  getStoredOnDuty,
  getStoredProfile,
  setStoredDriver,
  setStoredOnDuty,
} from '../../utils/session';
import { subscribePush } from '../../utils/webpush';
import { DELIVERY_COLUMNS } from '../../services/deliveries';
import {
  findLegacyDriverIdCookie,
  metersBetween,
  requestBrowserNotificationPermission,
  sendBrowserNotification,
} from './driverUtils';

export function useDriverRuntime() {
  const navigate = useNavigate();
  const toast = useToast();
  const [driver, setDriver]           = useState(null);
  const [deliveries, setDeliveries]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [isOnDuty, setIsOnDuty]       = useState(() => getStoredOnDuty());
  const [gpsStatus, setGpsStatus]     = useState(null); // 'ok' | 'error' | null
  const [wakeLockActive, setWakeLockActive] = useState(false);

  const driverRef           = useRef(null);
  const deliveryChannelRef  = useRef(null);
  const gpsWatchRef         = useRef(null);
  const gpsHeartbeatRef     = useRef(null);
  const gpsRetryRef         = useRef(null);
  const lastPosSentRef      = useRef(null);
  const wakeLockRef         = useRef(null);
  const isProfileBased = String(getStoredProfile()?.role || '') === 'driver';

  useEffect(() => { driverRef.current = driver; }, [driver]);

  // ── Screen Wake Lock ────────────────────────────────────────────────────────
  // Maintient l'écran allumé pendant le service GPS.
  // Sans ça, les navigateurs mobiles gèlent watchPosition dès que l'écran s'éteint.
  const acquireWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      if (wakeLockRef.current && !wakeLockRef.current.released) return;
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      setWakeLockActive(true);
      wakeLockRef.current.addEventListener('release', () => {
        wakeLockRef.current = null;
        setWakeLockActive(false);
      });
    } catch {
      // Appareil non supporté ou permission refusée — silencieux
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    wakeLockRef.current?.release().catch(() => {});
    wakeLockRef.current = null;
    setWakeLockActive(false);
  }, []);

  // ── Cleanup ─────────────────────────────────────────────────────────────────
  const cleanupDeliveryChannel = useCallback(() => {
    if (deliveryChannelRef.current) {
      supabase.removeChannel(deliveryChannelRef.current);
      deliveryChannelRef.current = null;
    }
  }, []);

  // ── GPS ─────────────────────────────────────────────────────────────────────
  const upsertLocation = useCallback((currentDriver, lat, lng) => {
    lastPosSentRef.current = { lat, lng };
    supabase.from('driver_locations').upsert(
      { driver_id: currentDriver.id, lat, lng, updated_at: new Date().toISOString() },
      { onConflict: 'driver_id' }
    ).then(({ error }) => {
      if (error) { console.error('GPS upsert:', error); setGpsStatus('error'); }
      else        { setGpsStatus('ok'); }
    });
  }, []);

  const stopGpsWatch = useCallback(() => {
    if (gpsWatchRef.current != null) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
      gpsWatchRef.current = null;
    }
    clearInterval(gpsHeartbeatRef.current);
    gpsHeartbeatRef.current = null;
    clearTimeout(gpsRetryRef.current);
    gpsRetryRef.current = null;
  }, []);

  const startGpsWatch = useCallback((currentDriver) => {
    if (!navigator.geolocation) return;
    stopGpsWatch();

    // watchPosition : déclenché à chaque mouvement > 20 m
    gpsWatchRef.current = navigator.geolocation.watchPosition(
      ({ coords: { latitude: lat, longitude: lng } }) => {
        if (metersBetween(lastPosSentRef.current, { lat, lng }) > 20) {
          upsertLocation(currentDriver, lat, lng);
        }
      },
      (err) => {
        console.warn('GPS watch:', err.message);
        setGpsStatus('error');
        // Auto-retry dans 10 s si erreur de timeout ou réseau
        if (err.code !== 1) { // code 1 = permission refusée → pas de retry
          clearTimeout(gpsRetryRef.current);
          gpsRetryRef.current = setTimeout(() => startGpsWatch(currentDriver), 10000);
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );

    // Heartbeat toutes les 15 s : force un envoi même sans mouvement
    gpsHeartbeatRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => upsertLocation(currentDriver, coords.latitude, coords.longitude),
        (err) => console.warn('GPS heartbeat:', err.message),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
      );
    }, 15000);
  }, [stopGpsWatch, upsertLocation]);

  const setDriverOnline = useCallback((currentDriver, logLabel) => {
    supabase.from('drivers').update({ status: 'available' }).eq('id', currentDriver.id)
      .then(({ error }) => { if (error) console.error(logLabel, error); });
  }, []);

  const recordAttendanceEnd = useCallback(async (driverId) => {
    const today = new Date().toISOString().split('T')[0];
    await supabase.from('attendance_logs')
      .update({ ended_at: new Date().toISOString() })
      .eq('driver_id', driverId)
      .eq('date', today);
  }, []);

  const startDutyFor = useCallback((currentDriver, { manual = false } = {}) => {
    if (!navigator.geolocation) {
      if (manual) toast('GPS non disponible sur cet appareil', 'error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsOnDuty(true);
        setStoredOnDuty(true);
        setDriverOnline(currentDriver, manual ? 'Driver status:' : 'Auto driver status:');
        upsertLocation(currentDriver, position.coords.latitude, position.coords.longitude);
        startGpsWatch(currentDriver);
        acquireWakeLock(); // 🔒 Maintenir l'écran allumé
        if (manual) toast('Tu es en service · GPS actif', 'success');
      },
      (error) => {
        setGpsStatus('error');
        if (!manual) {
          setIsOnDuty(true);
          setStoredOnDuty(true);
          setDriverOnline(currentDriver, 'Auto driver status:');
          startGpsWatch(currentDriver);
          acquireWakeLock();
          return;
        }
        if (error.code === 1) toast('Autorise la localisation dans les réglages du navigateur', 'error');
        else if (error.code === 3) toast('Délai GPS dépassé — réessaie dans une zone ouverte', 'error');
        else toast('Impossible d\'obtenir ta position GPS', 'error');
      },
      { enableHighAccuracy: true, timeout: manual ? 15000 : 10000, maximumAge: 30000 }
    );
  }, [setDriverOnline, startGpsWatch, acquireWakeLock, toast, upsertLocation]);

  // ── Livraisons realtime ───────────────────────────────────────────────────
  const subscribeToDriverDeliveries = useCallback((driverId) => {
    cleanupDeliveryChannel();
    const channel = supabase
      .channel(`realtime:driver-${driverId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'deliveries', filter: `driver_id=eq.${driverId}` },
        (payload) => {
          if (String(payload?.new?.confirmation_status || '') !== 'confirmed') return;
          setDeliveries((prev) => prev.some(d => d.id === payload.new.id) ? prev : [payload.new, ...prev]);
          toast('Nouvelle livraison assignée', 'info');
          const d = payload.new;
          sendBrowserNotification('Nouvelle livraison',
            `${d.client || 'Client'} · ${d.address || ''} · ${d.amount ? Number(d.amount).toLocaleString('fr-FR') + ' FCFA' : ''}`);
        })
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'deliveries', filter: `driver_id=eq.${driverId}` },
        (payload) => {
          const isConfirmed = String(payload?.new?.confirmation_status || '') === 'confirmed';
          if (isConfirmed) {
            setDeliveries((prev) => prev.some(d => d.id === payload.new.id)
              ? prev.map(d => d.id === payload.new.id ? payload.new : d)
              : [payload.new, ...prev]);
          } else {
            setDeliveries((prev) => prev.filter(d => d.id !== payload.new.id));
          }
        })
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'deliveries' },
        (payload) => { setDeliveries((prev) => prev.filter(d => d.id !== payload.old.id)); })
      .subscribe();
    deliveryChannelRef.current = channel;
  }, [cleanupDeliveryChannel, toast]);

  const fetchDeliveries = useCallback(async (driverId) => {
    if (!driverId) return;
    setLoading(true);
    const { data } = await supabase
      .from('deliveries').select(DELIVERY_COLUMNS)
      .eq('driver_id', driverId)
      .eq('confirmation_status', 'confirmed')
      .order('created_at', { ascending: false });
    if (data) setDeliveries(data);
    setLoading(false);
  }, []);

  // ── Cleanup au démontage ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopGpsWatch();
      releaseWakeLock();
      cleanupDeliveryChannel();
    };
  }, [cleanupDeliveryChannel, stopGpsWatch, releaseWakeLock]);

  // ── Reprise GPS + Wake Lock au retour au premier plan ────────────────────
  // Sur mobile, watchPosition, setInterval et le wake lock sont tous libérés
  // dès que l'écran s'éteint ou que l'app passe en arrière-plan.
  // Ces 3 listeners redémarrent TOUT dès que l'app revient au premier plan.
  useEffect(() => {
    const resume = () => {
      const currentDriver = driverRef.current;
      if (!currentDriver || !getStoredOnDuty()) return;
      startGpsWatch(currentDriver);
      acquireWakeLock(); // Ré-acquérir après retour (release automatique quand écran éteint)
      navigator.geolocation?.getCurrentPosition(
        ({ coords }) => upsertLocation(currentDriver, coords.latitude, coords.longitude),
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
    };

    const onVisibility = () => { if (document.visibilityState === 'visible') resume(); };
    const onPageShow   = (e) => { if (e.persisted) resume(); }; // iOS BFCache restore

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', resume);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', resume);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [startGpsWatch, acquireWakeLock, upsertLocation]);

  // ── Chargement du driver ─────────────────────────────────────────────────
  useEffect(() => {
    const loadDriver = async (currentDriver) => {
      setDriver(currentDriver);
      subscribePush(supabase, { role: 'driver', driverId: String(currentDriver.id) });
      fetchDeliveries(currentDriver.id);
      subscribeToDriverDeliveries(currentDriver.id);
      if (getStoredOnDuty() || currentDriver.status !== 'offline') {
        startDutyFor(currentDriver);
      }
    };

    const loadStoredDriver = async () => {
      const storedDriver = getStoredDriver();
      if (storedDriver) { await loadDriver(storedDriver); return; }

      const profile = getStoredProfile();
      if (profile?.role === 'driver' && profile.driver_id) {
        const { data } = await supabase.from('drivers').select('*').eq('id', profile.driver_id).single();
        if (!data) { navigate(APP_ROUTES.home, { replace: true }); return; }
        await loadDriver(data);
        return;
      }

      const legacyDriverId = findLegacyDriverIdCookie();
      if (!legacyDriverId) { navigate(APP_ROUTES.home, { replace: true }); return; }

      const { data } = await supabase.from('drivers').select('*').eq('id', legacyDriverId).single();
      if (!data) { navigate(APP_ROUTES.home, { replace: true }); return; }
      setStoredDriver(data);
      await loadDriver(data);
    };

    loadStoredDriver();
  }, [fetchDeliveries, navigate, startDutyFor, subscribeToDriverDeliveries]);

  // ── Actions publiques ────────────────────────────────────────────────────
  const startDuty = useCallback(() => {
    const currentDriver = driverRef.current;
    if (!currentDriver) return;
    requestBrowserNotificationPermission();
    startDutyFor(currentDriver, { manual: true });
  }, [startDutyFor]);

  const refreshGps = useCallback(() => {
    const currentDriver = driverRef.current;
    if (!currentDriver || !navigator.geolocation) return;
    setGpsStatus(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        upsertLocation(currentDriver, position.coords.latitude, position.coords.longitude);
        toast('Position GPS mise à jour', 'success');
      },
      (error) => {
        setGpsStatus('error');
        if (error.code === 1) toast('Autorise la localisation dans les réglages', 'error');
        else toast('Impossible d\'obtenir la position GPS', 'error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [upsertLocation, toast]);

  const endDuty = useCallback(async () => {
    stopGpsWatch();
    releaseWakeLock();
    setIsOnDuty(false);
    setStoredOnDuty(false);
    const currentDriver = driverRef.current;
    if (currentDriver) {
      await supabase.from('drivers').update({ status: 'offline' }).eq('id', currentDriver.id);
      recordAttendanceEnd(currentDriver.id);
      toast('Tu es hors service', 'info');
    }
  }, [stopGpsWatch, releaseWakeLock, toast, recordAttendanceEnd]);

  const updateStatus = useCallback(async (deliveryId, newStatus, label, returnReason) => {
    const now = new Date().toISOString();
    const isCompletion = newStatus === 'delivered' || newStatus === 'failed';
    const dbUpdates = isCompletion
      ? { status: newStatus, delivered_at: now, ...(newStatus === 'failed' && returnReason ? { return_reason: returnReason } : {}) }
      : { status: newStatus };

    setDeliveries((prev) => prev.map(d => d.id === deliveryId ? { ...d, ...dbUpdates } : d));
    toast(label, newStatus === 'delivered' ? 'success' : newStatus === 'failed' ? 'error' : 'info');

    const { error } = await supabase.from('deliveries').update(dbUpdates).eq('id', deliveryId);
    if (error) {
      toast(error.message || 'Erreur lors de la mise à jour', 'error');
      fetchDeliveries(driverRef.current?.id);
      return false;
    }

    if (isCompletion && driverRef.current) {
      const { data: remaining } = await supabase
        .from('deliveries').select('id')
        .eq('driver_id', driverRef.current.id)
        .in('status', ['pending', 'transit'])
        .neq('id', deliveryId);
      if ((!remaining || remaining.length === 0) && isOnDuty) {
        await supabase.from('drivers').update({ status: 'available' }).eq('id', driverRef.current.id);
      }
    }
    return true;
  }, [fetchDeliveries, isOnDuty, toast]);

  const handleLogout = useCallback(() => {
    stopGpsWatch();
    releaseWakeLock();
    if (driverRef.current) {
      supabase.from('drivers').update({ status: 'offline' }).eq('id', driverRef.current.id);
    }
    clearStoredDriverSession();
    navigate(APP_ROUTES.landing, { replace: true });
  }, [navigate, stopGpsWatch, releaseWakeLock]);

  return {
    driver,
    deliveries,
    loading,
    isOnDuty,
    isProfileBased,
    gpsStatus,
    wakeLockActive,
    setDeliveries,
    startDuty,
    endDuty,
    refreshGps,
    updateStatus,
    handleLogout,
  };
}
